import { addDocumentEventListener } from 'owa-event-listener';
import React from 'react';

const sections: {
    ref: React.RefObject<HTMLElement | null>;
    order: number;
}[] = [];

/** cool-down (ms) between successive F6 jumps */
const JUMP_COOLDOWN = 1000;
let lastJumpAt = 0;
let currentFocusJump = 0;

export enum HybridspaceSectionNavigation {
    NavigationHeader = 1,
    WorkGroupSideBar = 2,
    HomeDashboard = 3,
    PlacesBuildingPage = 4,
    PlacesAnalytics = 5,
    PlacesAnalyticsHvc = 6,
    PlacesAgenda = 100,
}

/**
 * Registers a page section and makes F6 jump to the next registered section.
 *
 * @param sectionRef ref of the container to focus
 * @param order      numeric priority; lower numbers are reached first
 *                   (default preserves previous “mount order” behaviour)
 */
export function useSectionNavigation(
    sectionRef: React.RefObject<HTMLElement | null>,
    order?: number
): void {
    React.useEffect(() => {
        const el = sectionRef.current;
        if (!el || order === undefined) {
            return;
        }

        // make sure the container is focusable
        if (!el.hasAttribute('tabindex')) {
            el.setAttribute('tabindex', '-1');
        }

        // register and keep list sorted by order
        sections.push({ ref: sectionRef, order });
        sections.sort((a, b) => a.order - b.order);

        return () => {
            const idx = sections.findIndex(s => s.ref === sectionRef);
            if (idx > -1) {
                sections.splice(idx, 1);
            }
        };
    }, [sectionRef, order]);

    React.useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            if (e.key !== 'F6' || sections.length === 0) {
                return;
            }

            const now = Date.now();
            if (now - lastJumpAt < JUMP_COOLDOWN) {
                return; // still in cool-down
            }

            const next = sections[currentFocusJump]?.ref.current;
            if (next) {
                e.preventDefault();
                next.focus();
                lastJumpAt = now;
                currentFocusJump = (currentFocusJump + 1) % sections.length; // cycle through sections
            }
        }

        const removeKeydownListener = addDocumentEventListener(
            'useSectionNavigation',
            'keydown',
            onKeyDown,
            document,
            true
        );
        return () => removeKeydownListener?.();
    }, []);
}
